import React, { useState, useRef, useCallback } from "react";

const SYMBOL_MULTIPLIERS = {
  "7️⃣": 50,
  "💎": 30,
  "🔔": 20,
  "⭐": 15,
  "🍉": 10,
  "🍇": 8,
  "🍋": 6,
  "🍒": 4,
};
const SYMBOLS = Object.keys(SYMBOL_MULTIPLIERS);
const BET_OPTIONS = [10, 20, 50];
const BULB_COUNT = 18;
const STARTING_BALANCE = 1000;

function randomSymbol() {
  return SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
}

// Rarer, higher-value symbols land less often when we hand the player a win.
function weightedWinSymbol() {
  const weights = SYMBOLS.map((s) => 1 / SYMBOL_MULTIPLIERS[s]);
  const total = weights.reduce((a, b) => a + b, 0);
  let r = Math.random() * total;
  for (let i = 0; i < SYMBOLS.length; i++) {
    r -= weights[i];
    if (r <= 0) return SYMBOLS[i];
  }
  return SYMBOLS[SYMBOLS.length - 1];
}

function playTone(freq, duration, type = "sine", gain = 0.05) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    g.gain.value = gain;
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start();
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
    osc.stop(ctx.currentTime + duration);
    osc.onended = () => ctx.close();
  } catch (e) {
    /* audio not available - fail silently */
  }
}

export default function SlotMachine() {
  const [reels, setReels] = useState([
    randomSymbol(),
    randomSymbol(),
    randomSymbol(),
  ]);
  const [spinning, setSpinning] = useState([false, false, false]);
  const [isPulling, setIsPulling] = useState(false);
  const [busy, setBusy] = useState(false);
  const [win, setWin] = useState(false);
  const [message, setMessage] = useState("PULL THE LEVER");
  const [balance, setBalance] = useState(STARTING_BALANCE);
  const [selectedBet, setSelectedBet] = useState(BET_OPTIONS[0]);
  const [coins, setCoins] = useState([]);
  const timers = useRef([]);
  const coinIdRef = useRef(0);

  const clearTimers = () => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
  };

  const spinReel = (index, finalSymbol, totalDuration) => {
    let elapsed = 0;
    let delay = 55;
    setSpinning((s) => {
      const n = [...s];
      n[index] = true;
      return n;
    });

    const tick = () => {
      setReels((r) => {
        const n = [...r];
        n[index] = randomSymbol();
        return n;
      });
      elapsed += delay;
      delay *= 1.07; // decelerate for a natural slow-down
      if (elapsed < totalDuration) {
        timers.current.push(setTimeout(tick, delay));
      } else {
        setReels((r) => {
          const n = [...r];
          n[index] = finalSymbol;
          return n;
        });
        setSpinning((s) => {
          const n = [...s];
          n[index] = false;
          return n;
        });
        playTone(170 + index * 45, 0.16, "triangle", 0.06);
      }
    };
    timers.current.push(setTimeout(tick, delay));
  };

  const spawnCoinShower = () => {
    const count = 26;
    const shower = Array.from({ length: count }).map(() => ({
      id: coinIdRef.current++,
      left: 4 + Math.random() * 88,
      delay: Math.random() * 0.5,
      duration: 1.1 + Math.random() * 0.7,
      drift: (Math.random() - 0.5) * 40,
    }));
    setCoins(shower);
    timers.current.push(setTimeout(() => setCoins([]), 2600));
  };

  const startSpin = useCallback(
    (bet) => {
      if (busy) return;
      setBusy(true);
      setWin(false);
      setCoins([]);
      setMessage("GOOD LUCK...");
      clearTimers();
      setBalance((b) => b - bet);

      const forceWin = Math.random() < 0.22;
      const winSymbol = weightedWinSymbol();
      const finals = forceWin
        ? [winSymbol, winSymbol, winSymbol]
        : [randomSymbol(), randomSymbol(), randomSymbol()];

      const durations = [1100, 1650, 2200];
      finals.forEach((sym, i) => spinReel(i, sym, durations[i]));

      const totalTime = Math.max(...durations) + 200;
      timers.current.push(
        setTimeout(() => {
          setBusy(false);
          const allMatch = finals[0] === finals[1] && finals[1] === finals[2];
          if (allMatch) {
            const multiplier = SYMBOL_MULTIPLIERS[finals[0]];
            const winnings = bet * multiplier;
            setBalance((b) => b + winnings);
            setWin(true);
            setMessage(`JACKPOT! TRIPLE ${finals[0]} — +${winnings}`);
            spawnCoinShower();
            playTone(660, 0.5, "square", 0.05);
            setTimeout(() => playTone(880, 0.45, "square", 0.05), 150);
            setTimeout(() => playTone(990, 0.4, "square", 0.05), 300);
          } else {
            setMessage("PULL THE LEVER");
          }
        }, totalTime),
      );
    },
    [busy],
  );

  const handlePull = () => {
    if (busy || isPulling) return;
    setIsPulling(true);
    playTone(300, 0.08, "square", 0.04);
    const bet = selectedBet;
    // reels kick off just before the lever fully bottoms out, like the real thing
    timers.current.push(setTimeout(() => startSpin(bet), 220));
  };

  const handleLeverAnimEnd = () => setIsPulling(false);

  const anySpinning = spinning.some(Boolean);

  return (
    <div className="sm-wrapper">
      <style>{CSS}</style>

      <div className="sm-stage">
        <div className="sm-paytable">
          <div className="sm-paytable-title">PAYTABLE</div>
          <div className="sm-paytable-sub">MATCH 3 SYMBOLS</div>
          <ul className="sm-paytable-list">
            {SYMBOLS.map((sym) => (
              <li key={sym}>
                <span className="sm-paytable-symbol">
                  {sym}
                  {sym}
                  {sym}
                </span>
                <span className="sm-paytable-mult">
                  ×{SYMBOL_MULTIPLIERS[sym]}
                </span>
              </li>
            ))}
          </ul>
          <div className="sm-paytable-footnote">multiplier × bet</div>
        </div>

        <div
          className={`sm-cabinet ${anySpinning ? "is-spinning" : ""} ${win ? "is-win" : ""}`}
        >
          <div className="sm-marquee">
            <div className="sm-bulbs">
              {Array.from({ length: BULB_COUNT }).map((_, i) => (
                <span
                  key={i}
                  className="sm-bulb"
                  style={{ animationDelay: `${i * 0.07}s` }}
                />
              ))}
            </div>
            <div className="sm-title">
              <span className="sm-title-icon" aria-hidden="true">
                🎰
              </span>
              <span>SLOT MACHINE</span>
            </div>
          </div>

          <div className="sm-screen-frame">
            <div className="sm-screen">
              {reels.map((sym, i) => (
                <div
                  className={`sm-reel-window ${spinning[i] ? "spinning" : ""}`}
                  key={i}
                >
                  <div className="sm-reel-shadow-top" />
                  <div className="sm-symbol">{sym}</div>
                  <div className="sm-reel-shadow-bottom" />
                </div>
              ))}
            </div>
            <div className="sm-payline" />
          </div>

          <div className={`sm-message ${win ? "win" : ""}`}>{message}</div>

          <div className="sm-controls">
            <div className="sm-balance">
              BALANCE
              <span className="sm-balance-value">
                {balance.toLocaleString()}
              </span>
            </div>
            <div className="sm-bet-group">
              <span className="sm-bet-label">BET</span>
              {BET_OPTIONS.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  className={`sm-bet-btn ${selectedBet === amt ? "active" : ""}`}
                  onClick={() => setSelectedBet(amt)}
                  disabled={busy}
                >
                  {amt}
                </button>
              ))}
            </div>
          </div>

          <div className="sm-base">
            <div className="sm-coin-slot" />
          </div>

          <div className="sm-coin-shower" aria-hidden="true">
            {coins.map((c) => (
              <span
                key={c.id}
                className="sm-coin"
                style={{
                  left: `${c.left}%`,
                  animationDelay: `${c.delay}s`,
                  animationDuration: `${c.duration}s`,
                  "--drift": `${c.drift}px`,
                }}
              >
                🪙
              </span>
            ))}
          </div>
        </div>

        <div className="sm-lever-assembly">
          <div className="sm-lever-plate" />
          <button
            type="button"
            className="sm-lever-btn"
            onClick={handlePull}
            disabled={busy}
            aria-label="Pull lever to spin the reels"
          >
            <span
              className={`sm-lever-arm ${isPulling ? "pulled" : ""}`}
              onAnimationEnd={handleLeverAnimEnd}
            >
              <span className="sm-lever-knob" />
              <span className="sm-lever-rod" />
            </span>
          </button>
          <div className="sm-lever-pivot" />
        </div>
      </div>
    </div>
  );
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bungee&family=Oswald:wght@400;600&display=swap');

html, body, #root {
  height: 100%;
  margin: 0;
  padding: 0;
  background: #050304;
}

.sm-wrapper {
  --gold: #d4af37;
  --gold-light: #f4dd8c;
  --gold-dark: #8a6b1a;
  --burgundy: #7a1626;
  --burgundy-dark: #4a0d17;
  --burgundy-light: #a3283c;
  --near-black: #0a0a0c;
  --cream: #f6e7c1;
  --neon-red: #ff2b4d;
  --chrome-1: #e8ecef;
  --chrome-2: #9aa3ad;
  --chrome-3: #4d545c;

  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  padding: 48px 24px;
  background: radial-gradient(ellipse at 50% 0%, #241417 0%, #120a0c 60%, #050304 100%);
  font-family: 'Oswald', sans-serif;
  box-sizing: border-box;
}

.sm-wrapper * { box-sizing: border-box; }

.sm-stage {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  flex-wrap: wrap;
  gap: 24px;
}

/* ---------- PAYTABLE ---------- */
.sm-paytable {
  width: 148px;
  border-radius: 10px;
  padding: 12px 12px 10px;
  background: linear-gradient(180deg, #1a0d10 0%, #0d0608 100%);
  border: 2px solid var(--gold);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.6), inset 0 2px 6px rgba(0, 0, 0, 0.6);
}

.sm-paytable-title {
  font-family: 'Bungee', 'Oswald', sans-serif;
  font-size: 15px;
  letter-spacing: 1px;
  color: var(--gold-light);
  text-align: center;
  text-shadow: 0 0 6px rgba(244, 221, 140, 0.4);
}

.sm-paytable-sub {
  font-size: 9px;
  letter-spacing: 1px;
  text-align: center;
  color: var(--cream);
  opacity: 0.65;
  margin-top: 2px;
  margin-bottom: 10px;
}

.sm-paytable-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sm-paytable-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 6px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.04);
}

.sm-paytable-symbol {
  font-size: 13px;
  letter-spacing: -1px;
}

.sm-paytable-mult {
  font-size: 12px;
  font-weight: 600;
  color: var(--gold-light);
}

.sm-paytable-footnote {
  margin-top: 10px;
  font-size: 8px;
  letter-spacing: 0.5px;
  text-align: center;
  color: var(--cream);
  opacity: 0.5;
}

/* ---------- CABINET ---------- */
.sm-cabinet {
  position: relative;
  width: 380px;
  border-radius: 20px 20px 14px 14px;
  padding: 18px 18px 26px;
  background:
    linear-gradient(180deg, var(--burgundy-light) 0%, var(--burgundy) 45%, var(--burgundy-dark) 100%);
  border: 3px solid var(--gold-dark);
  box-shadow:
    0 0 0 2px rgba(212, 175, 55, 0.35),
    0 25px 50px -12px rgba(0, 0, 0, 0.9),
    inset 0 2px 6px rgba(255, 255, 255, 0.15),
    inset 0 -20px 40px rgba(0, 0, 0, 0.45);
}

/* subtle brass rivets on the corners */
.sm-cabinet::before,
.sm-cabinet::after {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  top: 10px;
  background: radial-gradient(circle at 35% 30%, var(--gold-light), var(--gold-dark) 70%);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
}
.sm-cabinet::before { left: 10px; }
.sm-cabinet::after { right: 10px; }

/* ---------- MARQUEE ---------- */
.sm-marquee {
  position: relative;
  border-radius: 12px;
  padding: 14px 10px 18px;
  margin-bottom: 14px;
  background: linear-gradient(180deg, #1a0d10 0%, #0d0608 100%);
  border: 2px solid var(--gold);
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.7);
}

.sm-bulbs {
  display: flex;
  justify-content: space-between;
  padding: 0 6px 12px;
}

.sm-bulb {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #6b5417;
  box-shadow: inset 0 0 1px rgba(0, 0, 0, 0.6);
}

.is-spinning .sm-bulb {
  animation: bulb-chase 0.9s linear infinite;
}

@keyframes bulb-chase {
  0%, 100% {
    background: #6b5417;
    box-shadow: none;
  }
  15% {
    background: var(--gold-light);
    box-shadow: 0 0 8px 3px rgba(244, 221, 140, 0.9);
  }
  30% {
    background: #6b5417;
    box-shadow: none;
  }
}

.sm-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-family: 'Bungee', 'Oswald', sans-serif;
  font-size: 26px;
  letter-spacing: 1px;
  color: var(--cream);
  text-shadow:
    0 0 6px rgba(244, 221, 140, 0.5),
    0 2px 0 rgba(0, 0, 0, 0.6);
}

.sm-title-icon {
  font-size: 24px;
  filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.6));
}

/* ---------- SCREEN / REELS ---------- */
.sm-screen-frame {
  position: relative;
  padding: 14px;
  border-radius: 10px;
  background: linear-gradient(180deg, var(--chrome-1), var(--chrome-3));
  box-shadow:
    0 4px 10px rgba(0, 0, 0, 0.6),
    inset 0 0 0 2px rgba(255, 255, 255, 0.4);
}

.sm-screen {
  display: flex;
  gap: 10px;
  padding: 10px;
  border-radius: 6px;
  background: var(--near-black);
  box-shadow: inset 0 6px 14px rgba(0, 0, 0, 0.9), inset 0 -6px 14px rgba(0, 0, 0, 0.9);
}

.sm-reel-window {
  position: relative;
  flex: 1;
  height: 108px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 4px;
  background: radial-gradient(circle at 50% 40%, #171717 0%, #050505 100%);
  border: 1px solid rgba(212, 175, 55, 0.35);
}

.sm-reel-shadow-top,
.sm-reel-shadow-bottom {
  position: absolute;
  left: 0;
  right: 0;
  height: 26px;
  pointer-events: none;
  z-index: 2;
}
.sm-reel-shadow-top {
  top: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.85), transparent);
}
.sm-reel-shadow-bottom {
  bottom: 0;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.85), transparent);
}

.sm-symbol {
  font-size: 52px;
  line-height: 1;
  filter: drop-shadow(0 3px 3px rgba(0, 0, 0, 0.6));
  transform: scaleY(1) translateY(0);
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.15s linear;
}

.sm-reel-window.spinning .sm-symbol {
  filter: blur(4px) drop-shadow(0 0 2px rgba(255, 255, 255, 0.2));
  transform: scaleY(1.35);
}

.sm-payline {
  position: absolute;
  left: 14px;
  right: 14px;
  top: 50%;
  height: 2px;
  transform: translateY(-1px);
  background: rgba(255, 43, 77, 0.35);
  box-shadow: 0 0 4px rgba(255, 43, 77, 0.4);
  pointer-events: none;
}

.is-win .sm-payline {
  background: var(--neon-red);
  box-shadow: 0 0 12px 3px var(--neon-red), 0 0 24px 6px rgba(255, 43, 77, 0.6);
  animation: payline-pulse 0.6s ease-in-out infinite;
}

@keyframes payline-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.is-win .sm-reel-window {
  animation: reel-glow 0.8s ease-in-out infinite;
}

@keyframes reel-glow {
  0%, 100% { box-shadow: 0 0 0 rgba(255, 43, 77, 0); }
  50% { box-shadow: 0 0 18px 4px rgba(255, 43, 77, 0.55); }
}

/* ---------- MESSAGE ---------- */
.sm-message {
  margin-top: 14px;
  text-align: center;
  font-family: 'Oswald', sans-serif;
  font-weight: 600;
  letter-spacing: 2px;
  font-size: 14px;
  color: var(--gold-light);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.7);
  min-height: 18px;
}

.sm-message.win {
  color: var(--neon-red);
  animation: message-flash 0.5s ease-in-out infinite;
}

@keyframes message-flash {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.75; transform: scale(1.04); }
}

/* ---------- CONTROLS (balance + bet) ---------- */
.sm-controls {
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.15));
  border: 1px solid rgba(212, 175, 55, 0.4);
}

.sm-balance {
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 1px;
  color: var(--cream);
  opacity: 0.85;
  white-space: nowrap;
}

.sm-balance-value {
  display: block;
  color: var(--gold-light);
  font-size: 15px;
  letter-spacing: 0.5px;
  text-shadow: 0 0 6px rgba(244, 221, 140, 0.4);
}

.sm-bet-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.sm-bet-label {
  font-size: 10px;
  letter-spacing: 1px;
  color: var(--cream);
  opacity: 0.7;
  margin-right: 2px;
}

.sm-bet-btn {
  font-family: 'Oswald', sans-serif;
  font-weight: 600;
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 5px;
  cursor: pointer;
  background: linear-gradient(180deg, var(--chrome-1), var(--chrome-3));
  border: 1px solid rgba(0, 0, 0, 0.5);
  color: #16181a;
  transition: transform 0.1s ease, box-shadow 0.15s ease;
}

.sm-bet-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.sm-bet-btn.active {
  background: linear-gradient(180deg, var(--gold-light), var(--gold-dark));
  box-shadow: 0 0 8px 2px rgba(212, 175, 55, 0.7);
  color: #2a1c04;
}

.sm-bet-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* ---------- BASE ---------- */
.sm-base {
  margin-top: 16px;
  height: 22px;
  border-radius: 6px;
  background: linear-gradient(180deg, var(--burgundy), var(--burgundy-dark));
  border: 1px solid var(--gold-dark);
  display: flex;
  align-items: center;
  justify-content: center;
}

.sm-coin-slot {
  width: 50px;
  height: 8px;
  border-radius: 4px;
  background: #050304;
  box-shadow: inset 0 2px 3px rgba(0, 0, 0, 0.9), 0 1px 0 rgba(255, 255, 255, 0.08);
}

/* ---------- COIN SHOWER ---------- */
.sm-coin-shower {
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: 18px;
  pointer-events: none;
  z-index: 6;
}

.sm-coin {
  position: absolute;
  top: -30px;
  font-size: 22px;
  animation-name: coin-fall;
  animation-timing-function: cubic-bezier(0.55, 0, 1, 0.45);
  animation-fill-mode: forwards;
  filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.6));
  will-change: transform, opacity;
}

@keyframes coin-fall {
  0% {
    transform: translate(0, 0) rotate(0deg);
    opacity: 1;
  }
  85% {
    opacity: 1;
  }
  100% {
    transform: translate(var(--drift), 640px) rotate(560deg);
    opacity: 0;
  }
}

/* ---------- LEVER ---------- */
.sm-lever-assembly {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 56px;
  height: 340px;
  justify-content: flex-end;
  padding-bottom: 30px;
  margin-left: -20px;
}

.sm-lever-plate {
  position: absolute;
  bottom: 18px;
  width: 46px;
  height: 60px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--chrome-1), var(--chrome-3));
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.6), inset 0 0 0 2px rgba(255, 255, 255, 0.3);
  z-index: 0;
}

.sm-lever-btn {
  position: relative;
  z-index: 1;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  height: 300px;
  width: 40px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.sm-lever-btn:disabled {
  cursor: not-allowed;
}

.sm-lever-arm {
  position: relative;
  transform-origin: bottom center;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
}

.sm-lever-arm.pulled {
  animation: lever-pull 0.65s cubic-bezier(0.36, 1.5, 0.64, 1) both;
}

@keyframes lever-pull {
  0%   { transform: rotate(0deg); }
  32%  { transform: rotate(40deg); }
  55%  { transform: rotate(28deg); }
  75%  { transform: rotate(34deg); }
  100% { transform: rotate(0deg); }
}

.sm-lever-rod {
  flex: 1;
  width: 10px;
  border-radius: 6px;
  margin-top: -2px;
  background: linear-gradient(90deg, var(--chrome-3), var(--chrome-1) 45%, var(--chrome-3));
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.5);
}

.sm-lever-knob {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: radial-gradient(circle at 32% 28%, #ff8a93, var(--neon-red) 55%, #8c0018 100%);
  box-shadow:
    0 3px 6px rgba(0, 0, 0, 0.6),
    inset -3px -3px 6px rgba(0, 0, 0, 0.35),
    inset 2px 2px 4px rgba(255, 255, 255, 0.5);
}

.sm-lever-btn:disabled .sm-lever-knob {
  filter: saturate(0.5) brightness(0.85);
}

.sm-lever-pivot {
  position: absolute;
  bottom: 40px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, var(--chrome-1), var(--chrome-3) 75%);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
  z-index: 2;
}

/* ---------- RESPONSIVE ---------- */
@media (max-width: 700px) {
  .sm-paytable { order: 3; width: 380px; }
  .sm-cabinet { order: 1; }
  .sm-lever-assembly { order: 2; margin-left: 0; }
}

@media (max-width: 480px) {
  .sm-cabinet { width: 300px; }
  .sm-symbol { font-size: 40px; }
  .sm-reel-window { height: 84px; }
  .sm-title { font-size: 20px; }
  .sm-lever-assembly { height: 280px; width: 44px; }
  .sm-paytable { width: 300px; }
}

@media (prefers-reduced-motion: reduce) {
  .sm-lever-arm.pulled,
  .is-spinning .sm-bulb,
  .is-win .sm-payline,
  .is-win .sm-reel-window,
  .sm-message.win,
  .sm-coin {
    animation: none !important;
  }
  .sm-symbol { transition: none !important; }
}
`;
